import { Alignment, Checkbox, Icon, Label, Switch } from '@blueprintjs/core';
import { IconNames } from '@blueprintjs/icons';
import classNames from 'classnames';
import * as React from 'react';
import { Text, Tooltip } from 'vet-bones/components';
import { emptyArray } from 'vet-bones/constants';
import { alphaSort } from 'vet-bones/utils';
import { stringToSelectItem } from 'vet-bones/utils/select';

import {
  TEST_IDS,
  getPublishProjectDrawerModelAliasRowTestId,
} from 'src/constants/testIds';
import {
  useDefaultModelAliasInfo,
  useModelAliasNamePlaceholder,
} from 'src/containers/PublishProjectDrawer/hooks';
import { ModelMappingForm } from 'src/containers/PublishProjectDrawer/ModelMappingForm';
import { NamedGraphDeletionWarning } from 'src/containers/PublishProjectDrawer/NamedGraphDeletionWarning';
import {
  CreationType,
  ModelAliasInfo,
  ModelsInfo,
} from 'src/containers/PublishProjectDrawer/types';
import { dataModelItemsSelector } from 'src/selectors/dataModelItems';
import { ModelSelectItem } from 'src/selectors/modelItems';
import { namedGraphsSelector } from 'src/selectors/namedGraphs';
import { projectSelector } from 'src/selectors/project';
import { replaceableModelItemsSelector } from 'src/selectors/replaceableModelItems';
import * as copy from 'src/templates/copy';
import { getDanglingNamedGraphs } from 'src/utils/getDanglingNamedGraphs';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

type ModelAliasFormRowProps = {
  idx: number;
  isChecked: boolean;
  isDisabled: boolean;
  modelNames: string[];
  namedGraphIri: string;
  handleToggleIsChecked: (isChecked: boolean) => void;
};

const ModelAliasFormRow: React.VFC<ModelAliasFormRowProps> = ({
  handleToggleIsChecked,
  idx,
  isChecked,
  isDisabled,
  modelNames,
  namedGraphIri,
}) => (
  <div
    className="sd-model-alias-row"
    data-testid={getPublishProjectDrawerModelAliasRowTestId(namedGraphIri, idx)}
  >
    <div className="sd-model-alias-row__body">
      <div className="sd-model-alias-row-grid">
        <div className="sd-model-alias-row-grid__checkbox">
          <Checkbox
            checked={isChecked}
            data-testid={TEST_IDS.publishProjectDrawer.model.checkbox}
            disabled={isDisabled}
            onChange={() => {
              handleToggleIsChecked(!isChecked);
            }}
          />
        </div>
        <div className="sd-model-alias-row-grid__text">
          <Text ellipsize>{namedGraphIri}</Text>
        </div>
        <div className="sd-model-alias-row-grid__text">
          <Text ellipsize>{modelNames.join(', ')}</Text>
        </div>
      </div>
    </div>
  </div>
);

type ModelAliasesFormProps = {
  databaseId: string;
  existingAliasModel: ModelSelectItem | null;
  isUsingAlias: boolean;
  modelAliasInfo: ModelAliasInfo;
  modelsInfo: ModelsInfo;
  setExistingAliasModel: (item: ModelSelectItem | null) => void;
  setIsUsingAlias: (isUsingAlias: boolean) => void;
  setModelAliasInfo: (modelAliasInfo: ModelAliasInfo) => void;
  validateModelName: (name: string) => string;
};

export const ModelAliasesForm: React.VFC<ModelAliasesFormProps> = ({
  setExistingAliasModel,
  setIsUsingAlias,
  setModelAliasInfo,
  databaseId,
  existingAliasModel,
  isUsingAlias,
  modelAliasInfo,
  modelsInfo,
  validateModelName,
}) => {
  const { projectId } = useRouterParams();
  const project = useReduxSelector(projectSelector(projectId));
  const dataModelItems = useReduxSelector(dataModelItemsSelector(databaseId));
  const namedGraphs = useReduxSelector(namedGraphsSelector(databaseId));

  // remote models
  const replaceableModelItems = useReduxSelector(
    replaceableModelItemsSelector(databaseId)
  );

  // local "publishing" models
  const localModelItems = React.useMemo(
    () =>
      Object.values(modelsInfo)
        .filter(({ shouldPublish }) => shouldPublish)
        .map(({ modelName, namedGraphIri }) => ({
          ...stringToSelectItem(modelName),
          namedGraphIris: [namedGraphIri],
        })),
    [modelsInfo]
  );

  // items (for replace selector) including ONLY remote models
  // - don't allow selecting locally publishing
  // - filter out dangling named graphs since we will unreference them on publish
  const aliasModelItems = React.useMemo(() => {
    const dangling = getDanglingNamedGraphs({
      dataModelItems,
      modelsInfo,
      namedGraphs,
    });

    return replaceableModelItems
      .filter(
        ({ value }) => !localModelItems.find((item) => item.value === value)
      )
      .map((modelItem) => {
        const namedGraphIris = modelItem.namedGraphIris.filter(
          (namedGraphIri) => {
            // filter out dangling named graphs
            const isDangling = Object.values(dangling).includes(namedGraphIri);
            return !isDangling;
          }
        );
        return {
          ...modelItem,
          namedGraphIris,
        };
      });
  }, [
    dataModelItems,
    localModelItems,
    modelsInfo,
    namedGraphs,
    replaceableModelItems,
  ]);

  const defaultModelAliasInfo = useDefaultModelAliasInfo(
    databaseId,
    modelsInfo
  );
  const modelAliasNamePlaceholder = useModelAliasNamePlaceholder();

  const areSomeBoxesChecked = Boolean(
    isUsingAlias && modelAliasInfo.namedGraphIris.length
  );

  const modelsByNamedGraphIris = React.useMemo(() => {
    const modelsByNamedGraphIris: Record<string, Set<string>> = {};

    [...aliasModelItems, ...localModelItems].forEach(
      ({ value: modelName, namedGraphIris }) => {
        namedGraphIris.forEach((namedGraphIri) => {
          const models = modelsByNamedGraphIris[namedGraphIri] || new Set();
          models.add(modelName);
          modelsByNamedGraphIris[namedGraphIri] = models;
        });
      }
    );

    return modelsByNamedGraphIris;
  }, [aliasModelItems, localModelItems]);

  const checkDisabled = React.useCallback(
    (modelAliasInfo: ModelAliasInfo, namedGraphIri: string) => {
      // if we're replacing a data model
      if (
        modelAliasInfo.creationType === CreationType.REPLACE &&
        dataModelItems.find(
          ({ value: modelName }) => modelName === modelAliasInfo.modelName
        )
      ) {
        const danglingNamedGraphIrisByModel = getDanglingNamedGraphs({
          dataModelItems,
          modelsInfo,
          modelAliasInfo: {
            ...modelAliasInfo,
            namedGraphIris: [],
          },
          namedGraphs,
        });
        // don't allow checking the graphs that would be removed
        if (
          danglingNamedGraphIrisByModel[modelAliasInfo.modelName] ===
          namedGraphIri
        ) {
          return true;
        }
      }

      return false;
    },
    [dataModelItems, modelsInfo, namedGraphs]
  );

  const handleSetModelAliasInfo = React.useCallback(
    (nextInfo: Partial<ModelAliasInfo>) => {
      const newModelAliasInfo = {
        ...defaultModelAliasInfo,
        ...modelAliasInfo,
        ...nextInfo,
      };
      const namedGraphIris = newModelAliasInfo.namedGraphIris.filter(
        (iri) => !checkDisabled(newModelAliasInfo, iri)
      );
      setModelAliasInfo({
        ...newModelAliasInfo,
        namedGraphIris,
      });
    },
    [checkDisabled, defaultModelAliasInfo, modelAliasInfo, setModelAliasInfo]
  );
  const handleChangeCreationType = React.useCallback(
    (creationType: CreationType) => {
      if (creationType === CreationType.NEW) {
        handleSetModelAliasInfo({
          creationType: CreationType.NEW,
          modelName: modelAliasNamePlaceholder,
          // select all publishing models
          namedGraphIris: Object.values(modelsInfo)
            .filter(({ shouldPublish }) => shouldPublish)
            .map(({ namedGraphIri }) => namedGraphIri),
        });
      } else {
        handleSetModelAliasInfo({
          creationType: CreationType.REPLACE,
          modelName: existingAliasModel?.value || '',
          // select all original models
          namedGraphIris: Object.entries(modelsByNamedGraphIris)
            .filter(([, models]) => models.has(existingAliasModel?.value || ''))
            .map(([namedGraphIri]) => namedGraphIri),
        });
      }
    },
    [
      existingAliasModel,
      handleSetModelAliasInfo,
      modelAliasNamePlaceholder,
      modelsByNamedGraphIris,
      modelsInfo,
    ]
  );
  const handleSelectExisting = React.useCallback(
    (existing: ModelSelectItem | null) => {
      setExistingAliasModel(existing);
      if (existing?.value) {
        handleSetModelAliasInfo({
          modelName: existing.value,
          // select all original models
          namedGraphIris: Object.entries(modelsByNamedGraphIris)
            .filter(([, models]) => models.has(existing.value))
            .map(([namedGraphIri]) => namedGraphIri),
        });
      }
    },
    [handleSetModelAliasInfo, modelsByNamedGraphIris, setExistingAliasModel]
  );
  const handleToggleCheckedForProjectModel = React.useCallback(
    (namedGraphIri: string, isChecked: boolean) => {
      const namedGraphIrisSet = new Set(modelAliasInfo.namedGraphIris);
      if (isChecked) {
        namedGraphIrisSet.add(namedGraphIri);
      } else {
        namedGraphIrisSet.delete(namedGraphIri);
      }
      handleSetModelAliasInfo({
        namedGraphIris: Array.from(namedGraphIrisSet),
      });
    },
    [handleSetModelAliasInfo, modelAliasInfo.namedGraphIris]
  );
  const handleToggleAllChecked = React.useCallback(() => {
    if (!project) {
      return;
    }

    if (areSomeBoxesChecked) {
      handleSetModelAliasInfo({
        namedGraphIris: emptyArray as any,
      });
    } else {
      handleSetModelAliasInfo({
        namedGraphIris: Object.keys(modelsByNamedGraphIris),
      });
    }
  }, [
    areSomeBoxesChecked,
    handleSetModelAliasInfo,
    modelsByNamedGraphIris,
    project,
  ]);
  const handleToggleIsUsingAlias = React.useCallback(() => {
    setIsUsingAlias(!isUsingAlias);
  }, [isUsingAlias, setIsUsingAlias]);

  return (
    <>
      <div className="sd-drawer-group-label">
        <Switch
          alignIndicator={Alignment.RIGHT}
          checked={isUsingAlias}
          className="sd-label-switch"
          data-testid={TEST_IDS.publishProjectDrawer.database.modelAlias.toggle}
          inline
          labelElement={
            <div className="sd-drawer-group-label__info-tag">
              <Text>
                {copy.components.publishProjectDrawer.modelAlias.label}
              </Text>
              <Tooltip
                content={
                  copy.components.publishProjectDrawer.modelAlias.labelHelp
                }
              >
                <Icon icon={IconNames.INFO_SIGN} />
              </Tooltip>
            </div>
          }
          onChange={handleToggleIsUsingAlias}
        />
      </div>
      {!isUsingAlias ? null : (
        <>
          <NamedGraphDeletionWarning
            databaseId={databaseId}
            filterModelNames={[existingAliasModel?.value || '']}
            modelAliasInfo={modelAliasInfo}
            modelsInfo={modelsInfo}
          />
          <ModelMappingForm
            databaseId={databaseId}
            existingModel={existingAliasModel}
            handleChangeCreationType={handleChangeCreationType}
            handleSelectExistingModel={handleSelectExisting}
            handleSetModelInfo={handleSetModelAliasInfo}
            modelInfo={modelAliasInfo}
            modelItems={aliasModelItems}
            modelNamePlaceholder={modelAliasNamePlaceholder}
            namedGraphIriPlaceholder=""
            validateModelName={validateModelName}
            validateNamedGraph={() => ''}
          />
          <div className="sd-drawer-group-label">
            <Label>
              {
                copy.components.publishProjectDrawer.modelAlias
                  .includedModelNamedGraphs
              }
            </Label>
          </div>
          <div
            className={classNames(
              'sd-model-alias-row-header',
              'sd-model-alias-row-grid'
            )}
          >
            <div className="sd-model-alias-row-grid__checkbox">
              <Checkbox
                checked={
                  modelAliasInfo.namedGraphIris.length ===
                  Object.keys(modelsByNamedGraphIris).length
                }
                indeterminate={Boolean(
                  modelAliasInfo.namedGraphIris.length &&
                    modelAliasInfo.namedGraphIris.length !==
                      Object.keys(modelsByNamedGraphIris).length
                )}
                onChange={handleToggleAllChecked}
              />
            </div>
            <div>
              {
                copy.components.publishProjectDrawer.modelAlias.columnHeader
                  .modelNamedGraph
              }
            </div>
            <div>
              {
                copy.components.publishProjectDrawer.modelAlias.columnHeader
                  .models
              }
            </div>
          </div>
          {Object.entries(modelsByNamedGraphIris)
            .sort(alphaSort(([namedGraphIri]) => namedGraphIri))
            .map(([namedGraphIri, modelNames], idx) => {
              const isChecked =
                modelAliasInfo.namedGraphIris.includes(namedGraphIri);
              const isDisabled = checkDisabled(modelAliasInfo, namedGraphIri);
              const handleToggleIsChecked = (isChecked: boolean) =>
                handleToggleCheckedForProjectModel(namedGraphIri, isChecked);
              return (
                <ModelAliasFormRow
                  key={namedGraphIri}
                  handleToggleIsChecked={handleToggleIsChecked}
                  idx={idx}
                  isChecked={isChecked}
                  isDisabled={isDisabled}
                  modelNames={Array.from(modelNames)}
                  namedGraphIri={namedGraphIri}
                />
              );
            })}
        </>
      )}
    </>
  );
};
